// Story 10.1 — Hook que suscribe el componente a un evento del WebSocket.
// Singleton de cliente; lazy connect via ws-client.
import { useEffect, useRef } from 'react';
import {
  createWsClient,
  type WsClient,
  type WsEnvelope,
  type WsEventHandler,
} from '@/lib/ws-client';

let singleton: WsClient | null = null;

function getClient(): WsClient {
  if (singleton) return singleton;
  singleton = createWsClient();
  return singleton;
}

/**
 * Suscribe `handler` a `eventName` (`'*'` para todos). El cleanup se ejecuta
 * en unmount o cuando cambian las deps. Si el handler hace setState, asegurarse
 * de que el componente está montado (React 18+ batching lo cubre).
 *
 * Inyectable para tests via `client` opcional.
 */
export function useWsEvent(
  eventName: string,
  handler: (envelope: WsEnvelope) => void,
  client?: WsClient,
): void {
  // Estabilizar el handler — si el caller no usa `useCallback`, evitamos
  // unsubscribe/subscribe en cada render.
  const handlerRef = useRef<typeof handler>(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const c = client ?? getClient();
    const wrapper: WsEventHandler = (env) => handlerRef.current(env);
    return c.subscribe(eventName, wrapper);
  }, [eventName, client]);
}

/** Story 16.6 — reabre el socket con la sesión actual (tras rotarla). */
export function reconnectWs(): void {
  singleton?.reconnect();
}

/** Test hook: reset del singleton. NO usar en código de producción. */
export function __resetWsClientForTests(): void {
  singleton?.close();
  singleton = null;
}
